import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UIState, Theme, Direction, Language } from '@/shared/types';
import { storage } from '@/shared/lib/utils';
import { STORAGE_KEYS, BREAKPOINTS, DEFAULT_CURRENCY } from '@/shared/constants';

// Get initial values from storage or use defaults
const getInitialTheme = (): Theme => {
  const stored = storage.get<Theme>(STORAGE_KEYS.THEME);
  if (stored) return stored;

  // Default to light mode
  return 'light';
};

const getInitialLanguage = (): Language => {
  const stored = storage.get<Language>(STORAGE_KEYS.LANGUAGE);
  if (stored) return stored;

  // Check browser language
  const browserLang = navigator.language.split('-')[0];
  return browserLang === 'ar' ? 'ar' : 'en';
};

const getInitialDirection = (language: Language): Direction => {
  return language === 'ar' ? 'rtl' : 'ltr';
};

const getInitialSidebarState = (): boolean => {
  const stored = storage.get<boolean>(STORAGE_KEYS.SIDEBAR_STATE);
  if (stored !== null) return stored;

  // Default to open on desktop, closed on mobile
  if (typeof window !== 'undefined') {
    return window.innerWidth >= BREAKPOINTS.LG;
  }
  return true;
};

const initialLanguage = getInitialLanguage();

const initialState: UIState = {
  sidebarOpen: getInitialSidebarState(),
  sidebarHovered: false,
  theme: getInitialTheme(),
  direction: getInitialDirection(initialLanguage),
  language: initialLanguage,
  isMobile: typeof window !== 'undefined' ? window.innerWidth < BREAKPOINTS.LG : false,
  currencyCode: DEFAULT_CURRENCY,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
      storage.set(STORAGE_KEYS.SIDEBAR_STATE, state.sidebarOpen);
    },

    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
      storage.set(STORAGE_KEYS.SIDEBAR_STATE, action.payload);
    },

    setSidebarHovered: (state, action: PayloadAction<boolean>) => {
      state.sidebarHovered = action.payload;
    },

    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
      storage.set(STORAGE_KEYS.THEME, action.payload);

      // Apply theme to document
      const root = document.documentElement;
      root.classList.remove('light', 'dark');

      if (action.payload === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
        root.classList.add(systemTheme);
      } else {
        root.classList.add(action.payload);
      }
    },

    setLanguage: (state, action: PayloadAction<Language>) => {
      state.language = action.payload;
      state.direction = action.payload === 'ar' ? 'rtl' : 'ltr';
      storage.set(STORAGE_KEYS.LANGUAGE, action.payload);

      // Apply direction to document
      document.documentElement.dir = state.direction;
      document.documentElement.lang = action.payload;
    },

    setDirection: (state, action: PayloadAction<Direction>) => {
      state.direction = action.payload;
      document.documentElement.dir = action.payload;
    },

    setIsMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload;
      // Auto-close sidebar on mobile
      if (action.payload && state.sidebarOpen) {
        state.sidebarOpen = false;
      }
    },

    setCurrency: (state, action: PayloadAction<string>) => {
      state.currencyCode = action.payload;
    },

    initializeUI: (state) => {
      // Apply initial theme
      const root = document.documentElement;
      root.classList.remove('light', 'dark');

      if (state.theme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
        root.classList.add(systemTheme);
      } else {
        root.classList.add(state.theme);
      }

      // Apply initial direction
      document.documentElement.dir = state.direction;
      document.documentElement.lang = state.language;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  setSidebarHovered,
  setTheme,
  setLanguage,
  setDirection,
  setIsMobile,
  initializeUI,
  setCurrency,
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;

// Selectors
export const selectSidebarOpen = (state: { ui: UIState }) => state.ui.sidebarOpen;
export const selectSidebarHovered = (state: { ui: UIState }) => state.ui.sidebarHovered;
export const selectTheme = (state: { ui: UIState }) => state.ui.theme;
export const selectLanguage = (state: { ui: UIState }) => state.ui.language;
export const selectDirection = (state: { ui: UIState }) => state.ui.direction;
export const selectIsMobile = (state: { ui: UIState }) => state.ui.isMobile;
export const selectCurrencyCode = (state: { ui: UIState }) => state.ui.currencyCode;
// Computed selector: effective sidebar state (considering hover)
export const selectEffectiveSidebarOpen = (state: { ui: UIState }) =>
  state.ui.sidebarOpen || state.ui.sidebarHovered;
